/**
 * Layout for handwriting practice sheets.
 *
 * Each practice line is primary-ruled paper: a solid top line, a dashed midline and a
 * solid baseline, with a clear band under the baseline for descenders. Words sit on the
 * baseline as a solid model copy, then dashed (or pale) copies to trace, then empty
 * space to write in. Everything is in millimetres on a page from `pageGeometry`.
 */

import type { PageGeometry } from "@/lib/tool-kit/printables/page";
import {
  type FontKind,
  type FontSpec,
  type TextMeasure,
  textWidthMm,
  wrapText,
} from "@/lib/tool-kit/printables/text";

export type TraceStyle = "dashed" | "pale";

export interface HandwritingOptions {
  /** Writing height, top line to baseline, in millimetres. */
  height: number;
  fontKind: FontKind;
  /** 1 is the default letter size for the font; 0.9–1.2 for fine tuning. */
  sizeScale: number;
  /** A solid model copy at the start of each line. */
  model: boolean;
  /** Traced copies per line; 0 fills the line. */
  copies: number;
  /** Empty ruled lines after each entry. */
  blankRows: number;
  /** Space reserved at the top of page 1 for the title and the name line. */
  headerHeight: number;
}

export interface PlacedText {
  x: number;
  text: string;
  kind: "model" | "trace";
}

export interface HandwritingRow {
  /** y of the top line. */
  y: number;
  texts: PlacedText[];
}

export interface HandwritingPage {
  rows: HandwritingRow[];
}

export interface HandwritingLayout {
  pages: HandwritingPage[];
  /** Font size in millimetres (cap-to-baseline scaled for the font). */
  fontSize: number;
  /** Distance from one top line to the next. */
  pitch: number;
  /** Baseline y offset below the top line (equals the writing height). */
  height: number;
  font: FontSpec;
  /** Left and right of the ruled lines. */
  x1: number;
  x2: number;
}

/**
 * Font size as a multiple of the writing height. Sans-serif lowercase then reaches about
 * the midline and capitals about three quarters of the way to the top line; script faces
 * have a much smaller x-height and need to be set larger.
 */
export const FONT_FIT: Record<FontKind, number> = {
  sans: 1,
  serif: 1,
  script: 1.3,
};

export const MAX_ENTRIES = 80;
export const MAX_ENTRY_LENGTH = 120;
export const MAX_PAGES = 24;

/** Entries from pasted text: one per line, trimmed, long ones cut. */
export function parseEntries(raw: string): string[] {
  return raw
    .split("\n")
    .map((line) => line.trim().slice(0, MAX_ENTRY_LENGTH))
    .filter((line) => line.length > 0)
    .slice(0, MAX_ENTRIES);
}

/** The clear band under the baseline, for descenders and the eye to rest. */
export function descenderGap(height: number): number {
  return Math.max(6, Math.round(height * 0.55 * 10) / 10);
}

interface Placement {
  rows: PlacedText[][];
  blank: number;
}

function placeEntry(
  text: string,
  options: HandwritingOptions,
  fontSize: number,
  font: FontSpec,
  measure: TextMeasure,
  x1: number,
  x2: number
): Placement {
  const available = x2 - x1;
  const width = textWidthMm(measure, text, font, fontSize);
  if (width <= available) {
    const gap = fontSize * 0.6;
    const texts: PlacedText[] = [];
    let x = x1;
    if (options.model) {
      texts.push({ x, text, kind: "model" });
      x += width + gap;
    }
    let copies = 0;
    const limit =
      options.copies > 0 ? options.copies : Number.POSITIVE_INFINITY;
    while (copies < limit && x + width <= x2 + 0.01) {
      texts.push({ x, text, kind: "trace" });
      x += width + gap;
      copies += 1;
    }
    return { rows: [texts], blank: options.blankRows };
  }
  // Too long for one line: wrap it, one traced line per row.
  const lines = wrapText(measure, text, font, fontSize, available);
  const rows = lines.map((line, index) => [
    {
      x: x1,
      text: line,
      kind:
        options.model && index === 0 ? ("model" as const) : ("trace" as const),
    },
  ]);
  return { rows, blank: options.blankRows };
}

/** Flows entries onto pages. With no entries the sheet is blank primary-ruled paper. */
export function layoutHandwriting(
  entries: readonly string[],
  options: HandwritingOptions,
  geometry: PageGeometry,
  measure: TextMeasure
): HandwritingLayout {
  const font: FontSpec = { kind: options.fontKind };
  const fontSize =
    Math.round(
      options.height * FONT_FIT[options.fontKind] * options.sizeScale * 100
    ) / 100;
  const gap = descenderGap(options.height);
  const pitch = options.height + gap;
  const x1 = geometry.left;
  const x2 = geometry.left + geometry.contentWidth;
  const bottom = geometry.top + geometry.contentHeight;

  const lines: PlacedText[][] = [];
  if (entries.length === 0) {
    const capacity = Math.max(
      1,
      Math.floor(
        (geometry.contentHeight - options.headerHeight - options.height) / pitch
      ) + 1
    );
    for (let i = 0; i < capacity; i += 1) {
      lines.push([]);
    }
  }
  for (const entry of entries) {
    const placed = placeEntry(entry, options, fontSize, font, measure, x1, x2);
    lines.push(...placed.rows);
    for (let i = 0; i < placed.blank; i += 1) {
      lines.push([]);
    }
  }

  const pages: HandwritingPage[] = [];
  let current: HandwritingRow[] = [];
  let y = geometry.top + options.headerHeight;
  for (const texts of lines) {
    if (y + options.height > bottom + 0.01 && current.length > 0) {
      pages.push({ rows: current });
      if (pages.length >= MAX_PAGES) {
        current = [];
        break;
      }
      current = [];
      y = geometry.top;
    }
    current.push({ y, texts });
    y += pitch;
  }
  if (current.length > 0) {
    pages.push({ rows: current });
  }
  if (pages.length === 0) {
    pages.push({ rows: [] });
  }
  return { pages, fontSize, pitch, height: options.height, font, x1, x2 };
}
